import React from 'react'
import cls from 'classnames'
import styled from 'styled-components'

import { locales } from 'utils/languages'
import S from 'utils/sentences'

import {
  ANNOUNCEMENT_HEADER,
  EMAIL_FREQUENCY_DAILY,
  EMAIL_FREQUENCY_OFF,
  EMAIL_FREQUENCY_OPTIONS,
  INCLUDE_SPAM_USER_NOTIFICATIONS,
  mapPlaceholderKeyByTrigger,
  MSP_MAILBOX_COUNT_APPROACHING_LIMIT,
  MSP_NOTIFICATIONS,
  NOTIFY_END_USER_NOTIFICATIONS,
  NOTIFY_END_USER_NOTIFICATIONS_BY_FREQUENCY,
  REMEDIATED_EMAIL_REPORT_TRIGGER,
} from 'containers/Settings/Notifications/EndUser/constants'
import { RECIPIENTS_KEY } from 'containers/Settings/Notifications/EndUser/Editor/constants'

import CKEditor from 'components/elements/CKEditor'
import DataContainer from 'components/elements/DataContainer'
import Button from 'components/lib/DesignSystem/Button'
import Checkbox from 'components/lib/DesignSystem/Checkbox'
import { Participants, Refresh } from 'components/lib/DesignSystem/Icons'
import Input from 'components/lib/DesignSystem/Input'
import Modal from 'components/lib/DesignSystem/Modal'
import Notification from 'components/lib/DesignSystem/Notification'
import Select from 'components/lib/DesignSystem/Select'
import InputCounter from 'components/lib/InputCounter'

import RecipientsSelector from './RecipientsSelector'

class AnnouncementEditor extends React.Component {
  state = {
    text: '',
    template: '',
    subject: '',
    contactEmail: '',
    trigger: 0,
    emailFrequency: 0,
    approachingLimitPercentage: null,
    dateFilter: null,
    editorSourceModeEnabled: false,
    includeSpam: false,
    askToRelease: true,
    localRecipientsData: {},
  }

  componentDidMount() {
    this.bindLocalToState(this.props.locale)

    const { trigger, includeSpam, askToRelease } = this.state

    if (trigger === REMEDIATED_EMAIL_REPORT_TRIGGER) {
      this.updateEmailsListContent(includeSpam, askToRelease)
    }
  }

  componentWillReceiveProps(nextProps) {
    if (
      nextProps.hasChangedRecipientsField &&
      nextProps.trigger === REMEDIATED_EMAIL_REPORT_TRIGGER &&
      nextProps.localeId === this.state.localeId &&
      nextProps.companyFilter === this.props.companyFilter
    ) {
      return
    }

    if (nextProps.text !== this.state.text) {
      this.setState({
        text: nextProps.text,
        subject: nextProps.subject,
        template: nextProps.template,
        trigger: nextProps.trigger,
      })
    }
    if (nextProps.locale_id !== this.state.localeId) {
      this.setState({ localeId: nextProps.locale_id })
    }
    if (this.props.locale.frequency !== nextProps.locale.frequency) {
      this.setState({ emailFrequency: nextProps.locale.frequency || 0 })
    }
    if (
      this.props.locale.approachingLimitPercentage !== nextProps.locale.approachingLimitPercentage
    ) {
      this.setState({ approachingLimitPercentage: nextProps.locale.approachingLimitPercentage })
    }
    if (this.props.locale.includeSpam !== nextProps.locale.includeSpam) {
      this.setState({ includeSpam: nextProps.locale.includeSpam || false })
    }
    if (this.props.locale.askToRelease !== nextProps.locale.askToRelease) {
      this.setState({ askToRelease: nextProps.locale.askToRelease || true })
    }
    if (this.props.locale.dateFilter !== nextProps.locale.dateFilter) {
      this.setState({ dateFilter: nextProps.locale.dateFilter })
    }
    this.bindLocalToState(nextProps.locale)

    const groupSettingsAlert = nextProps.groupSettingsAlerts?.find(
      (e) => e.announcement_id === nextProps.announcement_id
    )
    if (groupSettingsAlert && groupSettingsAlert.locale_id === nextProps.localeId) {
      this.setState({
        text: decodeURIComponent(groupSettingsAlert.text || ''),
        subject: groupSettingsAlert.subject,
        template: groupSettingsAlert.template,
        emailFrequency: groupSettingsAlert.emailFrequency,
        contactEmail: groupSettingsAlert.contactEmail,
        approachingLimitPercentage: groupSettingsAlert.approachingLimitPercentage,
        localeId: groupSettingsAlert.locale_id,
        includeSpam: groupSettingsAlert.includeSpam,
        askToRelease: groupSettingsAlert.askToRelease,
      })
    }
    if (
      (this.props.trigger !== nextProps.trigger &&
        nextProps.trigger === REMEDIATED_EMAIL_REPORT_TRIGGER) ||
      nextProps.companyFilter !== this.props.companyFilter
    ) {
      this.props.fetchRecipients()
    }
    if (
      nextProps.trigger === REMEDIATED_EMAIL_REPORT_TRIGGER &&
      nextProps.selectedRecipients !== this.props.selectedRecipients
    ) {
      this.setState({ localRecipientsData: { selected: nextProps.selectedRecipients } })
      this.props.fetchParticipantsData({
        groups: nextProps.selectedRecipients,
      })
    }

    if (nextProps.trigger === REMEDIATED_EMAIL_REPORT_TRIGGER) {
      const includeSpam =
        nextProps.locale.includeSpam !== this.state.includeSpam
          ? nextProps.locale.includeSpam
          : this.state.includeSpam

      const askToRelease =
        nextProps.locale.askToRelease !== this.state.askToRelease
          ? nextProps.locale.askToRelease
          : this.state.askToRelease

      this.updateEmailsListContent(includeSpam, askToRelease)
    }
  }

  bindLocalToState = (locale) => {
    if (locale?.data) {
      const template = locale.data.template || ''
      const decoded = decodeURIComponent(locale.data.text || template)
      const subject = locale.data.subject
      this.setState((prevstate) => ({
        template,
        text: decoded,
        subject,
        localeId: locale.locale_id,
        contactEmail: NOTIFY_END_USER_NOTIFICATIONS_BY_FREQUENCY.includes(prevstate.trigger)
          ? locale.contactEmail || ''
          : '',
        approachingLimitPercentage: locale.approachingLimitPercentage,
        emailFrequency: locale.frequency,
        includeSpam: locale.includeSpam,
        askToRelease: locale.askToRelease,
      }))
    }
  }

  handleChangeInput = (value) => this.setState({ text: value })

  validated = () => {
    const { subject, text, localeId } = this.state
    const { addToast } = this.props
    if (!subject) {
      addToast({ message: 'Email subject is empty', type: 'error' })
      return false
    }
    if (!text) {
      addToast({ message: 'Email content is empty', type: 'error' })
      return false
    }
    if (!localeId) {
      addToast({ message: 'Language is not valid', type: 'error' })
      return false
    }
    return true
  }

  updateText = () => {
    const {
      template,
      subject,
      contactEmail,
      emailFrequency,
      localeId,
      trigger,
      dateFilter,
      includeSpam,
      askToRelease,
      localRecipientsData,
      approachingLimitPercentage,
    } = this.state
    let renderedText = this.state.text.toString('html')
    if (localeId === locales.HEBREW) {
      renderedText = `<div dir="rtl">${renderedText}<div/>`
    }
    const text = encodeURIComponent(renderedText)
    if (!this.validated()) {
      return
    }
    const data = {
      text,
      subject,
      template,
      announcement_id: this.props.announcement_id,
      locale_id: localeId,
      emailFrequency,
      contactEmail,
      approachingLimitPercentage,
      trigger,
      dateFilter,
      includeSpam,
      askToRelease,
      localRecipientsData,
    }
    if (this.props.isGroupSettings) {
      this.props.handleGroupSettingsSave(data)
    } else {
      this.props.updateData(data)
    }
  }

  deleteLocale = () => {
    this.props.deleteLocale({
      announcement_id: this.props.announcement_id,
      locale_id: this.state.localeId,
      trigger: this.state.trigger,
    })
    document.getElementById('x-modal_close')?.click()
  }

  handleSubjectChange = (text) => this.setState({ subject: text.target.value })

  handleContactEmailChange = (val) => this.setState({ contactEmail: val.target.value })

  handleEmailFrequencyChange = (val) => this.setState({ emailFrequency: val })

  handleDateChange = (val) => this.setState({ dateFilter: val })

  handleApproachingLimitChange = (val) => {
    this.setState({ approachingLimitPercentage: val })
  }

  handleIncludeSpamChange = (val) => {
    this.setState({ includeSpam: val })
    if (this.state.trigger === REMEDIATED_EMAIL_REPORT_TRIGGER) {
      this.updateEmailsListContent(val, this.state.askToRelease)
    }
  }

  handleAskToReleaseChange = (val) => {
    this.setState({ askToRelease: val })
    if (this.state.trigger === REMEDIATED_EMAIL_REPORT_TRIGGER) {
      this.updateEmailsListContent(this.state.includeSpam, val)
    }
  }

  handleRestoreDefault = () => {
    const { locale } = this.props
    const template = locale.data.template || ''
    const decoded = decodeURIComponent(locale.data.defaultText || template)

    this.setState({
      text: decoded,
      subject: locale.data.defaultSubject,
    })
  }

  handleChangeLocalRecipients = (newSelection, field, currentSelection) => {
    const recipientsData = { ...currentSelection }

    recipientsData[field] = newSelection

    this.setState({ localRecipientsData: recipientsData })

    this.props.fetchParticipantsData({
      groups: recipientsData[RECIPIENTS_KEY.SELECTED],
    })
  }

  updateEmailsListContent(includeSpam, askToRelease) {
    this.setState((prevState) => {
      const parser = new DOMParser()
      const doc = parser.parseFromString(prevState.text, 'text/html')

      const emailsListDiv = doc.querySelector('#emails_list')

      if (emailsListDiv) {
        emailsListDiv.innerHTML = ''

        if (includeSpam) {
          if (askToRelease) {
            emailsListDiv.innerHTML = `
            <div style="display: flex; align-items: center; flex-direction: column; gap: 10px; margin-bottom: 10px;">
              <img src="/static/webapp/images/quarantine-email-report-phishing-with-button-section.svg" width='700px' height='200px' />
              <img src="/static/webapp/images/quarantine-email-report-spam-section.svg" width='700px' height='350px' />
            </div>
          `
          } else {
            emailsListDiv.innerHTML = `
            <div style="display: flex; align-items: center; flex-direction: column; gap: 10px; margin-bottom: 10px;">
              <img src="/static/webapp/images/quarantine-email-report-phishing-without-button-section.svg" width='700px' height='200px' />
              <img src="/static/webapp/images/quarantine-email-report-spam-section.svg" width='700px' height='350px' />
            </div>
          `
          }
        } else if (askToRelease) {
          emailsListDiv.innerHTML = `
          <div style="display: flex; flex-direction: column; align-items: center; margin-bottom: 10px;">
            <img src="/static/webapp/images/quarantine-email-report-phishing-with-button-section.svg" width='700px' height='200px'/>
          </div
        `
        } else {
          emailsListDiv.innerHTML = `
            <div style="display: flex; flex-direction: column; align-items: center; margin-bottom: 10px;">
              <img src="/static/webapp/images/quarantine-email-report-phishing-without-button-section.svg" width='700px' height='200px'/>
            </div
          `
        }
      }

      const updatedText = doc.body.innerHTML

      return { text: updatedText }
    })
  }

  render() {
    const {
      text,
      subject,
      contactEmail,
      emailFrequency,
      trigger,
      editorSourceModeEnabled,
      includeSpam,
      askToRelease,
      localRecipientsData,
      approachingLimitPercentage,
    } = this.state

    const {
      className,
      loader,
      localeId,
      handleOnLanguageChange,
      getLanguageOptions,
      permissions,
      isGroupSettings,
      isStaticSalesDemo,
      defaultLanguage,
      recipientsSelectorData,
      recipients,
      recipientsSearchFilter,
      changeRecipientsSearchFilter,
    } = this.props

    const languageOptions = getLanguageOptions()

    const languageValue = languageOptions.filter((e) => e.value === localeId).length
      ? localeId
      : defaultLanguage.name

    return (
      <DataContainer className={cls('announcements-editor', className)} loader={loader}>
        <div>
          {MSP_NOTIFICATIONS.includes(trigger) && (
            <Notification
              text="This alert is enabled for partner only, and will be sent to the partner admin users."
              type="info"
              id="Home_Dashboard_Mailboxes_Widget_Mailbox_Only_MSP_Admins_Notification"
            />
          )}
          {ANNOUNCEMENT_HEADER.includes(trigger) && (
            <div className="contact-email-container">
              <div className="left-container dib-vac">
                {(NOTIFY_END_USER_NOTIFICATIONS.includes(trigger) ||
                  MSP_NOTIFICATIONS.includes(trigger)) && (
                  <Checkbox
                    id="Settings_End_User_Alerts_Notify_End_User_Checkbox"
                    checked={emailFrequency !== 0}
                    labelText={
                      MSP_NOTIFICATIONS.includes(trigger) ? 'Activate Alert' : 'Notify End-User'
                    }
                    onChange={() =>
                      this.handleEmailFrequencyChange(
                        emailFrequency === 0
                          ? EMAIL_FREQUENCY_DAILY.value
                          : EMAIL_FREQUENCY_OFF.value
                      )
                    }
                  />
                )}
                {INCLUDE_SPAM_USER_NOTIFICATIONS.includes(trigger) && (
                  <>
                    <Checkbox
                      id="Settings_End_User_Alerts_Include_Spam_Checkbox"
                      checked={includeSpam}
                      onChange={() => this.handleIncludeSpamChange(!includeSpam)}
                      labelText="Include Spam"
                    />
                    <Checkbox
                      id="Settings_End_User_Alerts_Ask_To_Release_Checkbox"
                      checked={askToRelease}
                      onChange={() => this.handleAskToReleaseChange(!askToRelease)}
                      labelText='Include "Ask to release" button'
                    />
                  </>
                )}
              </div>
            </div>
          )}
          {NOTIFY_END_USER_NOTIFICATIONS_BY_FREQUENCY.includes(trigger) && (
            <div className="contact-email-container">
              <Select
                id="endUserAlerts-selectFrequency"
                labelText="Frequency"
                value={emailFrequency || 0}
                onChange={this.handleEmailFrequencyChange}
                options={EMAIL_FREQUENCY_OPTIONS}
                placeholder="Select Email Frequency"
                className="frequency-dropdown mr"
                optionFilterProp="label"
              />
              {trigger === MSP_MAILBOX_COUNT_APPROACHING_LIMIT && (
                <div className="subject-value">
                  <InputCounter
                    id="Settings_EndUserAlerts_ApproachingLimitPercentage_Input"
                    type="number"
                    onChange={this.handleApproachingLimitChange}
                    min={50}
                    value={approachingLimitPercentage}
                    max={100}
                    labelText="Approaching limit (%)"
                    className="frequency-dropdown mr"
                  />
                </div>
              )}
              {!isGroupSettings && trigger === REMEDIATED_EMAIL_REPORT_TRIGGER && (
                <>
                  <RecipientsSelector
                    id="Settings_End_User_Alerts_Recipients_Selector"
                    data={recipientsSelectorData}
                    selectedRecipientsUsersData={recipients.data}
                    alreadySelectedRecipients={localRecipientsData.selected}
                    onChangeHandler={this.handleChangeLocalRecipients}
                    searchTerm={recipientsSearchFilter}
                    onSearchHandler={changeRecipientsSearchFilter}
                  />
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      paddingTop: '25px',
                      marginRight: '15px',
                    }}
                  >
                    <Participants width="30px" hight="30px" />
                    <div>{`${recipients.total} Recipient${recipients.total === 1 ? '' : 's'}`}</div>
                  </div>
                </>
              )}
              {!MSP_NOTIFICATIONS.includes(trigger) && (
                <Input
                  id="Settings_End_User_Alerts_Contact_Email_Input"
                  value={contactEmail}
                  type="text"
                  className="contact-email-input"
                  onChange={this.handleContactEmailChange}
                  placeholder="Please enter an email address"
                  labelText="Notify on new challenged incident"
                />
              )}
              {/* <div>
                    <span className="sb mr">Duration:</span>
                    <DateDropdown
                      dateFilter={this.state.dateFilter || DATE_VALUES.ALL_TIME}
                      dateFilterChange={this.handleDateChange}
                      customClassName="date-filter event-filters"
                      id="Settings_End_User_Alerts_Date_Filter"
                    />
                  </div> */}
            </div>
          )}
        </div>
        <div className="announcements-editor-header">
          <div className="header-container">
            <div className="subject">
              <span className="label-name">Email Subject:</span>
              <div className="subject-value">
                <Input
                  id="Settings_EndUserAlerts_EmailSubject_Input"
                  type="text"
                  value={subject}
                  onChange={this.handleSubjectChange}
                />
              </div>
            </div>
            <div>
              <Select
                className="is-select float-right-action emailNotifications-languages"
                optionFilterProp="label"
                options={languageOptions}
                value={languageValue}
                onChange={handleOnLanguageChange}
                placeholder="Select Language"
                id="endUserAlerts-selectLanguage"
              />
              <Button
                id="endUserAlerts-restore"
                text="Restore To Default"
                styleType="secondary"
                LeftIconDS={Refresh}
                dsIconStyles={{ fill: '#0152CC' }}
                disabled={!permissions.admin}
                className="mr"
                onClick={this.handleRestoreDefault}
              />
            </div>
          </div>
        </div>
        <CKEditor
          id="announcement"
          value={text}
          configuration="email"
          placeholdersFields={`${mapPlaceholderKeyByTrigger[trigger] || 'endUserDefault'}`}
          onSourceModeToggle={(value) => this.setState({ editorSourceModeEnabled: value })}
          onChange={this.handleChangeInput}
          activeUpdateMode
        />
        <div className="announcement-actions">
          {!isGroupSettings && (
            <Modal
              trigger={
                <Button
                  id="Settings_End_User_Alerts_Delete"
                  text="Delete"
                  disabled={!permissions.admin || isStaticSalesDemo}
                  styleType={isGroupSettings ? 'tertiary' : 'secondary'}
                  className="mr"
                />
              }
              title="Are you sure?"
              modalType="warning"
              confirm={this.deleteLocale}
              id="Settings_End_User_Alerts_Delete_Modal"
            >
              <>Are you sure you want to delete this notification?</>
            </Modal>
          )}
          <Button
            id="emailNotifications-update"
            styleType={isGroupSettings && 'secondary'}
            text={isGroupSettings ? 'Apply' : 'Update'}
            disabled={editorSourceModeEnabled || !permissions.admin || isStaticSalesDemo}
            popup={
              editorSourceModeEnabled && {
                position: 'topRight',
                text: S.DISABLE_SOURCE_MODE,
              }
            }
            onClick={this.updateText}
          />
        </div>
      </DataContainer>
    )
  }
}

export default styled(AnnouncementEditor)`
  .subject-value {
    .withValidation__container {
      padding: 0;
    }
  }
`
